/**
 * Native capabilities with web fallbacks. The Capacitor plugins
 * (@capacitor/camera, @capacitor/geolocation) are imported dynamically on
 * native only, so the PWA bundle doesn't carry them. Native projects need
 * `npx cap sync` after plugin upgrades.
 */
import { isNative } from '../device';
import { isLowBandwidthConnection } from '../media/compress';

/** Native camera capture. Web uses <input type="file" capture> instead (see PhotoInput). */
export async function takeNativePhoto(): Promise<Blob | null> {
  if (!isNative()) return null;
  const lowBw = isLowBandwidthConnection();
  const [{ Camera, EncodingType }, { Capacitor }] = await Promise.all([import('@capacitor/camera'), import('@capacitor/core')]);
  const r = await Camera.takePhoto({
    quality: lowBw ? 76 : 82,
    targetWidth: lowBw ? 1280 : 1600,
    correctOrientation: true,
    encodingType: EncodingType.JPEG,
    saveToGallery: false,
    editable: 'no',
  });
  const src = r.webPath ?? (r.uri ? Capacitor.convertFileSrc(r.uri) : null);
  if (!src) return null;
  // The photo is compressed again on the device before upload (lib/media/compress.ts).
  return (await fetch(src)).blob();
}

export interface Position {
  lat: number;
  lng: number;
  accuracy: number;
  capturedAt: string;
}

export async function currentPosition(timeoutMs = 15_000): Promise<Position> {
  if (isNative()) {
    const { Geolocation } = await import('@capacitor/geolocation');
    const p = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 });
    return { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy, capturedAt: new Date().toISOString() };
  }
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) return reject(new Error('GEOLOCATION_UNAVAILABLE'));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy, capturedAt: new Date().toISOString() }),
      (e) => reject(new Error(e.code === 1 ? 'GEOLOCATION_DENIED' : 'GEOLOCATION_FAILED')),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}

/** Best-effort position for photo evidence; never blocks capture. */
export async function positionOrNull(timeoutMs = 4_000): Promise<Position | null> {
  try {
    return await currentPosition(timeoutMs);
  } catch {
    return null;
  }
}
